import React, { forwardRef } from 'react';
import * as Core from '../../core';
import { kebabCase } from 'lodash';
import { AccessControlWrapper } from '../AccessControlWrapper';
import { FAIcon } from '../fa-icon';
import { InfoTooltip } from '../InfoTooltip';
import { formatClassName, pickDataAttributes } from '../../utils';
import { WindowHelper } from '../../utils/WindowHelper';
import { DataAttributes } from '../../utils/pickDataAttributes';
import { Props } from './types';
import { LEGACY_STYLE_PROP } from './legacyStyleProp';

// Not in the public Props type: Core.ButtonGroup stashes the real `style` value here before Chakra's Group
// can clobber it.
type PropsWithLegacyStyleGuard = Props & { [LEGACY_STYLE_PROP]?: string };

/** renders a button */
export const Button = forwardRef<HTMLButtonElement, Props>((props, forwardedRef) => {
  const {
    block,
    branded,
    children,
    className,
    colorScheme = 'brand',
    cssStyle,
    data = {},
    disabled,
    download,
    fontTheme,
    href,
    icon,
    iconFamily,
    iconProps,
    label,
    leftIcon,
    onClick,
    permissions,
    requiredPermission,
    rightIcon,
    rightIconProps,
    style = 'primary',
    size = 'md',
    tooltipAttributes,
    variant,
    [LEGACY_STYLE_PROP]: legacyStyleGuard,
    ...additionalProps
  } = props as PropsWithLegacyStyleGuard;

  /**
   * Methods
   */
  const onInternalClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (additionalProps?.target === '_blank') {
      window.open(href, additionalProps?.target, 'noopener,noreferrer');
    } else if (href) {
      WindowHelper.redirect(href);
    }
    if (onClick) {
      onClick(event);
    }
  };

  const getIcon = (side: string | undefined, icon?: string, iconProps?: object) => {
    if (!icon || icon === 'none') {
      // Note: tech-debt - the Core.Icon does not handle `none` very well. Needs to be solved later
      return undefined;
    }
    // ADO-2950558: Core Icon is not being displayed correctly in Foundry apps
    // Temporariliy rendering FAIcon instead as we were before
    // return <Core.Icon {...iconProps} iconName={icon} />;
    // @ts-expect-error: the ...iconProps spread will overwrite icon={icon} will write a ticket to come back for this so it can be tested. Leaving this existing behavior as is for now
    return <FAIcon icon={icon} spin={!!icon && icon === 'spinner'} additionalClasses={side} {...iconProps} />;
  };

  // Prefer Core.ButtonGroup's pre-stashed value; otherwise guard against a Group-clobbered non-string `style`.
  const legacyStyle = legacyStyleGuard ?? (typeof style === 'string' ? style : 'primary');

  /**
   * DOM
   */
  const buttonClassName: string =
    formatClassName([
      fontTheme || 'ui-default',
      className,
      {
        [`btn-${legacyStyle}-branded`]: branded,
        'btn-block': block,
        'no-label': !label,
      },
    ]) || '';
  const dataProps: { [prop: string]: string | number } = {};
  if (data.action) {
    dataProps['data-action'] = data.action;
  }
  if (data.objectId) {
    dataProps['data-object-id'] = data.objectId;
  }
  if (data.objectType) {
    dataProps['data-object-type'] = data.objectType;
  }
  if (data.componentType) {
    dataProps['data-component-type'] = data.componentType;
  }
  if (href) {
    dataProps['data-href'] = href;
  } else {
    dataProps['data-component-type'] = label ? `${kebabCase(label)}-button` : 'button';
  }
  const dataAttributes: DataAttributes = pickDataAttributes(props);
  const buttonVariant = variant || legacyStyle; // Make sure variant gets precedence over style for non-legacy buttons
  const accessControlProps = {
    permissions,
    requiredPermission,
    ...additionalProps,
  };

  const downloadProps: any = {};

  if (download) {
    downloadProps.download = download;
    downloadProps.href = href;

    if (!href) {
      console.error(
        "'href' is required when using 'download', considering adding it or removing 'download' if you use onClick to perform the download process."
      );
    }
  }

  const foundryButtonProps = {
    ...additionalProps,
    ...dataProps,
    ...dataAttributes,
    ...downloadProps,
    colorScheme,
    className: buttonClassName,
    isDisabled: disabled,
    variant: buttonVariant,
    style: cssStyle,
    ref: forwardedRef,
    size,
  };

  const leftSlot = leftIcon ?? getIcon('left', icon, iconProps);

  return (
    <AccessControlWrapper {...accessControlProps}>
      <Core.Button {...foundryButtonProps} onClick={onInternalClick}>
        {leftSlot}
        {label ? <span className="btn-text">{label}</span> : children}
        {getIcon('right', rightIcon, rightIconProps)}
      </Core.Button>
      {tooltipAttributes?.tooltip && <InfoTooltip tooltipPosition="top" {...tooltipAttributes} />}
    </AccessControlWrapper>
  );
});

Button.displayName = 'ButtonUDL';
